/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { PinTypesDaoService } from '../pin-types-dao.service';
import {
  DbPersistenceService,
  Query
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { PinType } from 'src/app/_model/pin-type';
import { LocalPinTypeDbTableDefinition } from './local-pin-type-db-table-definition';
import { PinTypeDbTableDefinition } from './pin-type-db-table-definition';

@Injectable()
export class PinTypesDbDaoService implements PinTypesDaoService {

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private pinTypeTableDefinition: PinTypeDbTableDefinition,
    private localPinTypeTableDefinition: LocalPinTypeDbTableDefinition,
  ) { }

  public async savePinTypes(pinTypes: PinType[]): Promise<void> {
    this.logger.log(this.constructor.name, "Save pinTypes:", pinTypes);
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var pinTypeQueries: Query[] = [];
      for (const pinType of pinTypes) {
        let parameters;
        let values;
        let fields;
        let table;
       
        parameters = [
          pinType.id.imsId,
          pinType.databag ? JSON.stringify(pinType.databag) : undefined,
          pinType.name,
          pinType.description,
          pinType.icon,
        ];
        values = '(?,?,?,?,?)';
        fields = '(id, databag, name, description, icon)';
        table = this.pinTypeTableDefinition.tableName;
     
        let pinTypeQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            table + ' ' + 
            fields +
            ' VALUES ' +
            values,
          parameters: parameters
        };
        pinTypeQueries.push(pinTypeQuery);
      }

      const result = await this.dbPersistenceService.executeBatch(db, pinTypeQueries);
    } catch (error) {
      this.logger.log(
        'Error saving pinTypes to db',
        error,
        '-',
        error?.message
      );
    }
  }

  public async getPinTypes(): Promise<PinType[]> {
    this.logger.log(this.constructor.name, "get pinTypes");
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let pinTypes: PinType[] = [];

      // Get Synced Pins
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.pinTypeTableDefinition.tableName + ';',
        parameters: null
      });

      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
          pinTypes.push(
          PinType.fromDto({
            id: row['id'],
            name: row['name'],
            description: row['description'],
            icon: row['icon']
          }, row['databag'] ? JSON.parse(row['databag']) : undefined)
        );
      }

      // Get local generated Pins
      let localData = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.localPinTypeTableDefinition.tableName + ';',
        parameters: null
      });
      
      for (let i = 0; i < localData.values.length; i++) {
        let row = localData.values[i];
        pinTypes.push(
          PinType.fromDto({
            id: undefined,
            name: row['name'],
            description: row['description'],
            icon: row['icon'],
          })
        );
      }

      this.logger.log(this.constructor.name, "return pinTypes", pinTypes);
      return pinTypes;
    } catch (error) {
      this.logger.log('Error reading pinTypes from db', error);
      return undefined
    }
  }

  public async reset() {
    // Do nothing
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(this.constructor.name, "truncate table", this.pinTypeTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.pinTypeTableDefinition.tableName);
    this.logger.log(this.constructor.name, "truncate table", this.localPinTypeTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.localPinTypeTableDefinition.tableName);
  }

}
